import React, { useEffect } from "react";
import { FieldArray, FormikProvider, FormikErrors, useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store";
import CountryAutocomplete from "../../components/country-autocomplete";
import { MuiTelInput } from "mui-tel-input";

interface Shareholder {
  position: string;
  nationality: string;
  fullName: string;
  dateOfBirth: string;
  address: {
    country: string;
    address: string;
    zip: string;
  };
  email: string;
  mobileNumber: string;
  ownershipPercentage: number;
}

interface OwnershipFormValues {
  shareholders: Shareholder[];
}

const initialValues: OwnershipFormValues = {
  shareholders: [
    {
      position: "",
      nationality: "",
      fullName: "",
      dateOfBirth: "",
      address: { country: "", address: "", zip: "" },
      email: "",
      mobileNumber: "",
      ownershipPercentage: 0,
    },
  ],
};

const validationSchema = Yup.object().shape({
  shareholders: Yup.array()
    .of(
      Yup.object().shape({
        position: Yup.string().required("Required"),
        nationality: Yup.string().required("Required"),
        fullName: Yup.string().required("Required"),
        dateOfBirth: Yup.date()
          .required("Required")
          .typeError("Required")
          .max(
            new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
            "Shareholder must be at least 18 years old"
          )
          .min(
            new Date(new Date().setFullYear(new Date().getFullYear() - 110)),
            "Something wrong with age..."
          ),
        address: Yup.object().shape({
          country: Yup.string().required("Country is required"),
          address: Yup.string().required("Address is required"),
          zip: Yup.string().required("Zip code is required"),
        }),
        email: Yup.string().email("Invalid email").required("Required"),
        mobileNumber: Yup.string().required("Required"),
        ownershipPercentage: Yup.number()
          .required("Required")
          .min(0, "Must be at least 0"),
      })
    )
    .min(1, "Please add at least one shareholder")
    .test(
      "unique-emails",
      "Each shareholder must have a unique email",
      (shareholders) => {
        if (!shareholders) return true;
        const emails = shareholders.map((s) => s.email);
        return emails.length === new Set(emails).size;
      }
    )
    .test(
      "unique-phones",
      "Each shareholder must have a unique phone number",
      (shareholders) => {
        if (!shareholders) return true;
        const phones = shareholders.map((s) => s.mobileNumber);
        return phones.length === new Set(phones).size;
      }
    )
    .test(
      "sum-percentage",
      "Total ownership must be between 75 and 100",
      (shareholders) => {
        if (!shareholders) return false;
        const total = shareholders.reduce(
          (sum, shareholder) => sum + (shareholder.ownershipPercentage ?? 0),
          0
        );
        return total >= 75 && total <= 100;
      }
    ),
});

const CompanyInformationForm3 = () => {
  const navigate = useNavigate();
  const newUser = useAppSelector((state) => state.register.newUser);

  if (!newUser) {
    navigate("/signin");
  }

  const onSubmit = (values: OwnershipFormValues) => {
    const latestData = JSON.parse(localStorage.getItem("form3_json") || "{}");
    const mergedData = {
      ...latestData,
      shareholders: values.shareholders,
    };
    localStorage.setItem("form3_json", JSON.stringify(mergedData));
    navigate("/corp-info-4");
  };

  const formik = useFormik<OwnershipFormValues>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const errorText = (
    shareholderIndex: number,
    field: keyof Shareholder
  ): string => {
    const touchedShareholder = formik.touched.shareholders?.[shareholderIndex];
    const errorShareholder = formik.errors.shareholders?.[shareholderIndex];

    if (
      touchedShareholder &&
      errorShareholder &&
      typeof errorShareholder !== "string"
    ) {
      const fieldError = (errorShareholder as FormikErrors<Shareholder>)[field];
      return fieldError ? (fieldError as string) : "";
    }
    return "";
  };

  useEffect(() => {
    const stored = localStorage.getItem("ownershipStructure");
    if (!stored) return;

    try {
      const arr = JSON.parse(stored);
      if (Array.isArray(arr) && arr.length > 0) {
        const o = arr[0];
        formik.setValues({
          shareholders: [
            {
              position: "",
              nationality: o.nationality || "",
              fullName: o.fullName || "",
              dateOfBirth: o.date_of_birth || "",
              address: {
                country: o.country || "",
                address: o.address + " " + o.city + " " + o.state || "",
                zip: o.zip || "",
              },
              email: o.email || "",
              mobileNumber: o.phone || "",
              ownershipPercentage: 0,
            },
          ],
        });
      }
    } catch (e) {
      console.error("Cant parse:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormikProvider value={formik}>
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <Container maxWidth="md">
          <form onSubmit={formik.handleSubmit}>
            <Card
              elevation={24}
              component={motion.div}
              layoutId="ownership-card"
            >
              <CardContent>
                <Stack spacing={4}>
                  <Typography variant="h5" fontWeight="600">
                    Ownership Structure
                  </Typography>
                  <Typography>
                    Please confirm the names and addresses of the Ultimate
                    Beneficial Owners (UBOs) holding 25% and above and state
                    their percentage of ownership of the company.
                  </Typography>

                  <FieldArray name="shareholders">
                    {({ push, remove, form }) => (
                      <>
                        {form.values.shareholders.map(
                          (shareholder: Shareholder, index: number) => (
                            <Box key={index} sx={{ mt: 3 }}>
                              <Typography variant="h6">
                                Shareholder {index + 1}
                              </Typography>
                              <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    label="Position"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].position`
                                    )}
                                    error={!!errorText(index, "position")}
                                    helperText={errorText(index, "position")}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={8}>
                                  <TextField
                                    label="Full Name"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].fullName`
                                    )}
                                    error={!!errorText(index, "fullName")}
                                    helperText={errorText(index, "fullName")}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                  <CountryAutocomplete
                                    label="Nationality"
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].nationality`
                                    )}
                                    value={
                                      formik.values.shareholders[index]
                                        .nationality
                                    }
                                    kycProgramId={newUser?.kyc_program_id}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    label="Date of Birth"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].dateOfBirth`
                                    )}
                                    error={!!errorText(index, "dateOfBirth")}
                                    helperText={errorText(index, "dateOfBirth")}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                  {(() => {
                                    const ownProps = formik.getFieldProps(
                                      `shareholders[${index}].ownershipPercentage`
                                    );
                                    return (
                                      <TextField
                                        label="Ownership %"
                                        type="number"
                                        fullWidth
                                        {...ownProps}
                                        InputProps={{
                                          inputProps: {
                                            min: 0,
                                            max: 100,
                                            step: 0.01,
                                          },
                                          sx: {
                                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                              {
                                                WebkitAppearance: "none",
                                                margin: 0,
                                              },
                                          },
                                        }}
                                        onChange={(e) => {
                                          const numericValue = Number(
                                            e.target.value
                                          );
                                          ownProps.onChange({
                                            target: {
                                              name: ownProps.name,
                                              value: numericValue,
                                            },
                                          });
                                        }}
                                        error={
                                          !!errorText(
                                            index,
                                            "ownershipPercentage"
                                          )
                                        }
                                        helperText={errorText(
                                          index,
                                          "ownershipPercentage"
                                        )}
                                      />
                                    );
                                  })()}
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                  <CountryAutocomplete
                                    label="Country"
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].address.country`
                                    )}
                                    value={
                                      formik.values.shareholders[index].address
                                        .country
                                    }
                                    kycProgramId={newUser?.kyc_program_id}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    label="Address"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].address.address`
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    label="ZIP code"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].address.zip`
                                    )}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Email"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].email`
                                    )}
                                    error={!!errorText(index, "email")}
                                    helperText={errorText(index, "email")}
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <MuiTelInput
                                    forceCallingCode
                                    focusOnSelectCountry
                                    defaultCountry="US"
                                    label="Mobile Number"
                                    name={`shareholders[${index}].mobileNumber`}
                                    value={
                                      formik.values.shareholders[index]
                                        .mobileNumber
                                    }
                                    onChange={(value) =>
                                      formik.setFieldValue(
                                        `shareholders[${index}].mobileNumber`,
                                        value
                                      )
                                    }
                                    error={Boolean(
                                      formik.touched.shareholders?.[index]
                                        ?.mobileNumber
                                    )}
                                    helperText={errorText(
                                      index,
                                      "mobileNumber"
                                    )}
                                    fullWidth
                                  />
                                </Grid>
                              </Grid>
                              {form.values.shareholders.length > 1 && (
                                <Button
                                  variant="outlined"
                                  onClick={() => remove(index)}
                                  sx={{ mt: 1 }}
                                >
                                  Remove
                                </Button>
                              )}
                            </Box>
                          )
                        )}
                        <Button
                          variant="contained"
                          onClick={() =>
                            push({
                              position: "",
                              nationality: "",
                              fullName: "",
                              dateOfBirth: "",
                              address: { country: "", address: "", zip: "" },
                              email: "",
                              mobileNumber: "",
                              ownershipPercentage: 0,
                            })
                          }
                          sx={{ mt: 2 }}
                        >
                          Add Shareholder
                        </Button>
                        {typeof formik.errors.shareholders === "string" && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 2 }}
                          >
                            {formik.errors.shareholders}
                          </Typography>
                        )}
                      </>
                    )}
                  </FieldArray>

                  <Stack spacing={2} mt={3}>
                    <LoadingButton
                      loading={formik.isSubmitting}
                      type="submit"
                      variant="contained"
                    >
                      Save & Continue
                    </LoadingButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </form>
        </Container>
      </Box>
    </FormikProvider>
  );
};

export default CompanyInformationForm3;
